// Characteristics of Sound Waves — Grade 9 Physics.
// Schema mirrors photosynthesisLesson / quadraticLesson so it plugs into
// LessonStage + TeacherPanel unchanged. Each fragment includes:
//   - caption:           narration text used by Audio Studio (the EXACT
//                        narration provided by the lesson author).
//   - conceptCheck:      OPTIONAL — a brief check-for-understanding shown
//                        in a popup over this fragment with a close button.
//   - engagementQuestion: OPTIONAL — open-ended question shown the same way.
//
// Slide-level fields:
//   - pageBackground:    full-screen letterbox background (image url, color, or
//                        CSS gradient). Used by LessonStage's ScaledStage so the
//                        background extends edge-to-edge in fullscreen mode.
//                        density.

const SLIDES = [
    {
        id: "sw-hook",
        section: "concept",
        mode: "intro",
        title: "The Dance of Sound",
        estimatedSeconds: 30,
        tags: ["hook"],
        pageBackground: "#000000",
        fragments: [
            {
                caption:
                    "Welcome to 'The Dance of Sound'. We'll explore how chaotic vibrations actually transform into the structured music and clear voices we hear every day.",
            },
        ],
    },
    {
        id: "sw-longitudinal",
        section: "concept",
        mode: "diagram_teaching",
        title: "Seeing the Invisible",
        estimatedSeconds: 90,
        tags: ["compression", "rarefaction"],
        // Default to bottom-right; we override per-fragment below where
        // the slide's right half fills up with the chart card.
        pageBackground:
            "linear-gradient(135deg, #F8FAFC, #E2E8F0)",
        fragments: [
            {
                // Only the left explanation + 2 definition cards are
                caption:
                    "Sound travels as a longitudinal wave, much like a pulse moving through a slinky. It creates alternating regions of high-pressure compressions and low-pressure rarefactions in the air.",
            },
            {
                // Chart card now fills the right half — move to the
                // bottom-left where the definition cards have already
                // settled and there's still breathing room.
                caption:
                    "By plotting density against position, we translate this physical bunching into a clean sine curve. The high crests represent compressions, while the low troughs represent rarefactions.",
                conceptCheck:
                    "Which part of this sine curve represents the high-pressure region where air particles are crowded together?",
            },
        ],
    },
    {
        id: "sw-wavelength",
        section: "concept",
        mode: "diagram_teaching",
        title: "Measuring the Wave: Wavelength",
        estimatedSeconds: 90,
        tags: ["wavelength", "lambda"],
        pageBackground:
            "linear-gradient(135deg, #082037, #0C4A6E)",
        fragments: [
            {
                // Only the title + paragraph on left, big empty right.
                caption:
                    "To measure a wave's physical length, we look at the distance between two consecutive compressions or rarefactions. We call this the wavelength, denoted by the Greek letter Lambda.",
            },
            {
                // Chart card on right + λ card on left middle. Bottom-left
                caption:
                    "Since compressions map to the crests on our graph, measuring from one crest to the very next gives us the exact wavelength. It's one full repetition of the pattern.",
            },
            {
                // λ bracket now on bottom of chart; top-right of chart is
                caption:
                    "You get the exact same distance measuring from the bottom, too. Top-to-top or bottom-to-bottom, that length is always your wavelength, Lambda.",
                conceptCheck:
                    "If the distance from a crest to the very next trough is 10 centimeters, what is the total wavelength?",
            },
        ],
    },
    {
        id: "sw-frequency",
        section: "concept",
        mode: "diagram_teaching",
        title: "The Rhythm of Sound: Frequency & Time",
        estimatedSeconds: 110,
        tags: ["frequency", "time-period"],
        pageBackground:
            "linear-gradient(135deg, #FEF3C7, #FDE68A)",
        fragments: [
            {
                // One small frequency card top-left; rest of slide empty.
                caption:
                    "Now let's look at how sound behaves in time, which we call frequency. It's simply the number of complete vibrations that happen in just one second.",
            },
            {
                // Chart card appears top-right with counting helper pill.
                // Top-right now has the pill. Move to bottom-left.
                caption:
                    "Looking at the graph, we can count four complete wave cycles happening in exactly one second. That means this wave has a frequency of four Hertz.",
                engagementQuestion:
                    "If we squeezed eight cycles into this same one-second window, how would that change the sound we hear?",
            },
            {
                // Time-period card appears below freq card on left → bottom-left
                // now also filling up. Shift to bottom-right (chart is up).
                caption:
                    "Flipping our perspective, let's zoom in on just a single cycle. The time it takes to complete this one oscillation is called the Time Period, T.",
            },
            {
                // Formula card now bottom-left; chart still upper-right.
                caption:
                    "Frequency and Time Period actually have an inverse relationship. If you have tons of waves rushing by every second, the time for each individual wave must be very short.",
                conceptCheck:
                    "If a sound wave has a frequency of 50 Hertz, what is the Time Period for just one cycle?",
            },
        ],
    },
    {
        id: "sw-pitch",
        section: "concept",
        mode: "diagram_teaching",
        title: "Highs and Lows: Understanding Pitch",
        estimatedSeconds: 90,
        tags: ["pitch"],
        pageBackground: "#0F172A",
        fragments: [
            {
                // Definition banner at top only — rest empty.
                caption:
                    "Pitch is essentially how our brain interprets a wave's frequency. It's what lets us classify sounds as 'high' or 'low' on a musical scale.",
            },
            {
                // Low-pitch card on left + central chart card. Right column empty.
                caption:
                    "Look at how stretched out this blue wave is. That long wavelength means a low frequency, which we hear as a deep, low pitch like a booming drum.",
            },
            {
                // High-pitch card now appears on right — right column full.
                caption:
                    "Now compare it to the red wave, where the peaks are tightly packed. This fast, high-frequency vibration is what our ears perceive as a sharp, high pitch.",
            },
            {
                caption:
                    "So the rule is simple and direct. Faster vibrations mean a higher frequency, which creates a higher pitch.",
                conceptCheck:
                    "If you tighten a guitar string so it vibrates faster, how does the pitch change?",
            },
        ],
    },
    {
        id: "sw-amplitude",
        section: "concept",
        mode: "diagram_teaching",
        title: "Volume Control: Amplitude & Loudness",
        estimatedSeconds: 90,
        tags: ["amplitude", "loudness"],
        pageBackground:
            "linear-gradient(135deg, #F1F5F9, #E2E8F0)",
        fragments: [
            {
                // Just the intro card on left — right empty.
                caption:
                    "Shifting our focus to volume brings us to the concept of amplitude. Simply put, a sound's loudness is determined by the amplitude of its wave.",
            },
            {
                // Chart card appears top-right.
                caption:
                    "Notice how short this wave is from the center line to the peak? That small amplitude results in a very soft sound, like a gentle whisper.",
            },
            {
                // Large amplitude card appears on left → both columns fill.
                caption:
                    "When we increase the energy, the wave stretches vertically and becomes much taller. As you'd guess, this larger amplitude creates a significantly louder sound.",
                engagementQuestion:
                    "When you turn up the volume on your headphones, what exactly are you changing about the sound wave?",
            },
            {
                // Formula card now fills the left bottom. Top-right still ok.
                caption:
                    "Mathematically, loudness is proportional to the square of the amplitude. So if you double the amplitude, the sound actually becomes four times as intense!",
                conceptCheck:
                    "If we triple a wave's amplitude, how does its loudness change according to the math?",
            },
        ],
    },
    {
        id: "sw-timbre",
        section: "concept",
        mode: "diagram_teaching",
        title: "The Signature of Sound: Timbre",
        estimatedSeconds: 90,
        tags: ["timbre", "harmonics"],
        pageBackground:
            "linear-gradient(135deg, #2E1065, #6B21A8)",
        fragments: [
            {
                caption:
                    "Timbre is essentially the unique signature of a sound. A tuning fork produces the smooth, pure sine wave you see here because it consists of just a single frequency.",
            },
            {
                // Complex card appears below pure card → left fills.
                caption:
                    "Real instruments produce complex waves with ripples and bumps. This unique shape — a mix of the fundamental note and its harmonics — is what determines the instrument's distinct timbre.",
                conceptCheck:
                    "Would a pure whistle sound look more like the smooth cyan line or the jagged purple complex wave?",
            },
        ],
    },
    {
        id: "sw-wave-equation",
        section: "concept",
        mode: "concept_teaching",
        title: "Connecting the Dots: The Wave Equation",
        estimatedSeconds: 90,
        tags: ["equation"],
        pageBackground: "#FFFFFF",
        fragments: [
            {
                // Just the first card visible top-left of the 2x2 grid.
                caption:
                    "Let's connect the dots to derive the famous Wave Equation. We'll start with a familiar concept: speed is simply distance covered per unit of time.",
            },
            {
                // Second card top-right reveals.
                caption:
                    "For a single wave cycle, the distance traveled is the wavelength, Lambda. The time it takes to complete that cycle is the Time Period, T.",
            },
            {
                // Third card bottom-left.
                caption:
                    "By substituting these values into our basic speed formula, we see that velocity equals wavelength divided by the time period.",
            },
            {
                // and tucks top-right.
                caption:
                    "Since frequency is the reciprocal of the time period, we can swap it in to get the final Wave Equation. Speed equals wavelength times frequency.",
                conceptCheck:
                    "If a wave's speed is constant, what happens to its wavelength when the frequency is doubled?",
            },
        ],
    },
    {
        id: "sw-worked-example",
        section: "concept",
        mode: "solution_reveal",
        title: "Let's Solve It",
        estimatedSeconds: 110,
        tags: ["practice"],
        pageBackground: "#FFFBEB",
        fragments: [
            {
                // Problem statement banner at top — rest empty.
                caption:
                    "Let's see this equation in practice. We need to calculate how long it takes a sound wave with a two kilohertz frequency and thirty-five centimeter wavelength to travel one point five kilometers.",
            },
            {
                // Given-data card appears bottom-left.
                caption:
                    "First, we must convert our given values into standard SI units. Two kilohertz becomes two thousand Hertz, thirty-five centimeters becomes zero point three five meters, and one point five kilometers is fifteen hundred meters.",
            },
            {
                // Speed-calc card appears next to it.
                caption:
                    "Now we multiply our wavelength of zero point three five meters by the two thousand Hertz frequency. That gives us a wave speed of seven hundred meters per second.",
                conceptCheck:
                    "How would our calculated speed be affected if we forgot to convert the wavelength to meters?",
            },
            {
                // Time-calc card fills the row → all three side-by-side.
                caption:
                    "To find the time, we just divide our total distance of fifteen hundred meters by that speed of seven hundred. We get a final answer of approximately two point one four seconds.",
            },
        ],
    },
    {
        id: "sw-simulator",
        section: "concept",
        mode: "practice_activity",
        title: "Interactive Wave Simulator",
        estimatedSeconds: 120,
        tags: ["interactive"],
        pageBackground:
            "linear-gradient(135deg, #ECFEFF, #E0F2FE)",
        fragments: [
            {
                // Sliders col on the right side — bottom-right has the hint
                // of the way of both the chart and the controls.
                caption:
                    "Adjust the sliders to match the target wave and see these properties in action. Notice how increasing the frequency crowds the waves together, proving that inverse relationship with wavelength!",
                engagementQuestion:
                    "When adjusting the sliders, how does increasing amplitude visually differ from increasing frequency?",
            },
        ],
    },
    {
        id: "mcq-display",
        section: "concept-check",
        mode: "mcq_display",
        title: "Concept Check",
        estimatedSeconds: 60,
        tags: ["assessment"],
        notes: ["Wait at least 30s before revealing."],
        misconceptions: [],
        fragments: [{ caption: "Read the question carefully." }],
    },
    {
        id: "subjective-display",
        section: "practice",
        mode: "subjective_display",
        title: "Practice / Solved Example",
        estimatedSeconds: 120,
        tags: ["subjective"],
        notes: ["Walk the room as students think."],
        misconceptions: [],
        fragments: [{ caption: "Take a moment to think this through." }],
    },
    {
        id: "blank-slide",
        section: "tools",
        mode: "blank_slide",
        title: "Blank Teaching Slide",
        estimatedSeconds: 0,
        tags: ["whiteboard"],
        notes: ["Use the pen to draw / write — the AI can read it."],
        misconceptions: [],
        fragments: [{ caption: "" }],
    },
    {
        id: "real-life-example",
        section: "tools",
        mode: "real_life_example",
        title: "Real-life example",
        estimatedSeconds: 30,
        tags: ["analogy"],
        notes: ["Use vivid analogy to anchor the concept."],
        misconceptions: [],
        fragments: [{ caption: "" }],
    },
    {
        id: "sw-recap",
        section: "recap",
        mode: "recap",
        title: "The Music of Physics",
        estimatedSeconds: 60,
        tags: ["recap"],
        pageBackground: "#0F172A",
        fragments: [
            {
                // Only the centered hook quote visible — plenty of space.
                caption:
                    "Let's connect these technical terms back to our 'dancing paint' visual. It's the perfect way to see these invisible waves, as the paint reacts directly to the sound's physics.",
            },
            {
                // Left amplitude card appears.
                caption:
                    "Focus on the paint's jump height, which represents amplitude. A bigger physical motion translates directly to a louder sound for your ears.",
            },
            {
                // Right frequency card also appears → grid full at top.
                caption:
                    "Now look at the speed of the dance, which is the frequency. A faster vibration gives you high pitch notes, while a slower dance produces deep bass.",
            },
            {
                caption:
                    "It's just these two properties working in tandem. Together, the height and speed create the unique identity of every single sound we hear.",
                conceptCheck:
                    "If we cranked up the volume without changing the song, would the paint jump higher or dance faster?",
            },
        ],
    },
];


export const soundWavesStoryboard = [
    {
        "type": "say",
        "order": 0,
        "slide_no": 0,
        "fragment_no": 0,
        "slug": "lesson-opener",
        "text": "Today's lesson: Characteristics of Sound Waves. Amplitude, frequency, pitch, timbre — the anatomy of every note.",
        "narration": "Hi class, welcome! Today we're diving into Characteristics of Sound Waves. Amplitude, frequency, pitch, timbre — the anatomy of every note.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/KQf0om1VxR4HzNDli2NS5E39Owq3wFSTatXygFVB.wav",
        "durationMs": 10851
    },
    {
        "type": "say",
        "order": 1,
        "slide_no": 1,
        "fragment_no": 0,
        "slug": "sw-hook",
        "text": "Welcome to 'The Dance of Sound'. We'll explore how chaotic vibrations actually transform into the structured music and clear voices we hear every day.",
        "narration": "Welcome to 'The Dance of Sound'. We'll explore how chaotic vibrations actually transform into the structured music and clear voices we hear every day.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/e29uYZOCtnawvYn1yUOqMsVCq4qcEW2IIAYwt1TV.wav",
        "durationMs": 9291
    },
    {
        "type": "say",
        "order": 2,
        "slide_no": 2,
        "fragment_no": 0,
        "slug": "sw-longitudinal",
        "text": "Sound travels as a longitudinal wave, much like a pulse moving through a slinky. It creates alternating regions of high-pressure compressions and low-pressure rarefactions in the air.",
        "narration": "Sound travels as a longitudinal wave, much like a pulse moving through a slinky. It creates alternating regions of high-pressure compressions and low-pressure rarefactions in the air.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/HN2dqrRgFRbrUYU8RuldlYnFqUubMWu8jT8gJKpg.wav",
        "durationMs": 12491
    },
    {
        "type": "say",
        "order": 3,
        "slide_no": 2,
        "fragment_no": 1,
        "slug": "sw-longitudinal",
        "text": "By plotting density against position, we translate this physical bunching into a clean sine curve. The high crests represent compressions, while the low troughs represent rarefactions.",
        "narration": "By plotting density against position, we translate this physical bunching into a clean sine curve. The high crests represent compressions, while the low troughs represent rarefactions.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/CG1r9uuYqJXmv9U12LNV5YW1i9tdQCt5Hys7hmPo.wav",
        "durationMs": 11931
    },
    {
        "type": "say",
        "order": 4,
        "slide_no": 3,
        "fragment_no": 0,
        "slug": "sw-wavelength",
        "text": "To measure a wave's physical length, we look at the distance between two consecutive compressions or rarefactions. We call this the wavelength, denoted by the Greek letter Lambda.",
        "narration": "To measure a wave's physical length, we look at the distance between two consecutive compressions or rarefactions. We call this the wavelength, denoted by the Greek letter Lambda.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/a8d3EBe8BjSa6pGTcxLhbU2TAle1PG4P7bZKZvRZ.wav",
        "durationMs": 11211
    },
    {
        "type": "say",
        "order": 5,
        "slide_no": 3,
        "fragment_no": 1,
        "slug": "sw-wavelength",
        "text": "Since compressions map to the crests on our graph, measuring from one crest to the very next gives us the exact wavelength. It's one full repetition of the pattern.",
        "narration": "Since compressions map to the crests on our graph, measuring from one crest to the very next gives us the exact wavelength. It's one full repetition of the pattern.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/UeuaffPiDhkJcNk9B10RWvZ8ZkO0GZjuukKfC5Cr.wav",
        "durationMs": 10011
    },
    {
        "type": "say",
        "order": 6,
        "slide_no": 3,
        "fragment_no": 2,
        "slug": "sw-wavelength",
        "text": "You get the exact same distance measuring from the bottom, too. Top-to-top or bottom-to-bottom, that length is always your wavelength, Lambda.",
        "narration": "You get the exact same distance measuring from the bottom, too. Top-to-top or bottom-to-bottom, that length is always your wavelength, Lambda.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/EQRlVZlnJyTeu6GG5hRfULVZQmJZOWPpPo9znpcT.wav",
        "durationMs": 9811
    },
    {
        "type": "say",
        "order": 7,
        "slide_no": 4,
        "fragment_no": 0,
        "slug": "sw-frequency",
        "text": "Now let's look at how sound behaves in time, which we call frequency. It's simply the number of complete vibrations that happen in just one second.",
        "narration": "Now let's look at how sound behaves in time, which we call frequency. It's simply the number of complete vibrations that happen in just one second.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/OZgRv1uhxD46s4v0U5eOojD1cibaoHPervP5LJwc.wav",
        "durationMs": 9171
    },
    {
        "type": "say",
        "order": 8,
        "slide_no": 4,
        "fragment_no": 1,
        "slug": "sw-frequency",
        "text": "Looking at the graph, we can count four complete wave cycles happening in exactly one second. That means this wave has a frequency of four Hertz.",
        "narration": "Looking at the graph, we can count four complete wave cycles happening in exactly one second. That means this wave has a frequency of four Hertz.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/zMGohKjtuek1CAL0uGN1FRfEQa7j3WRxBHvjE7vx.wav",
        "durationMs": 10731
    },
    {
        "type": "say",
        "order": 9,
        "slide_no": 4,
        "fragment_no": 2,
        "slug": "sw-frequency",
        "text": "Flipping our perspective, let's zoom in on just a single cycle. The time it takes to complete this one oscillation is called the Time Period, T.",
        "narration": "Flipping our perspective, let's zoom in on just a single cycle. The time it takes to complete this one oscillation is called the Time Period, T.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/zIogRc1QuOszPjx7X00RqGWRxqXswYwly6zZJvxQ.wav",
        "durationMs": 10371
    },
    {
        "type": "say",
        "order": 10,
        "slide_no": 4,
        "fragment_no": 3,
        "slug": "sw-frequency",
        "text": "Frequency and Time Period actually have an inverse relationship. If you have tons of waves rushing by every second, the time for each individual wave must be very short.",
        "narration": "Frequency and Time Period actually have an inverse relationship. If you have tons of waves rushing by every second, the time for each individual wave must be very short.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/aSzpNC6DLpPNe9ISIsQ9OsE0bOqGZXSu20PNFdVW.wav",
        "durationMs": 10931
    },
    {
        "type": "say",
        "order": 11,
        "slide_no": 5,
        "fragment_no": 0,
        "slug": "sw-pitch",
        "text": "Pitch is essentially how our brain interprets a wave's frequency. It's what lets us classify sounds as 'high' or 'low' on a musical scale.",
        "narration": "Pitch is essentially how our brain interprets a wave's frequency. It's what lets us classify sounds as 'high' or 'low' on a musical scale.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/2gDqLM8aAZI4GXMUQ6wK7bOHl5WTbHZ2EqTlD7KC.wav",
        "durationMs": 9771
    },
    {
        "type": "say",
        "order": 12,
        "slide_no": 5,
        "fragment_no": 1,
        "slug": "sw-pitch",
        "text": "Look at how stretched out this blue wave is. That long wavelength means a low frequency, which we hear as a deep, low pitch like a booming drum.",
        "narration": "Look at how stretched out this blue wave is. That long wavelength means a low frequency, which we hear as a deep, low pitch like a booming drum.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/cM5N3czpnr53Jzee2n0ORSomtSGzhF03PZRE9van.wav",
        "durationMs": 10331
    },
    {
        "type": "say",
        "order": 13,
        "slide_no": 5,
        "fragment_no": 2,
        "slug": "sw-pitch",
        "text": "Now compare it to the red wave, where the peaks are tightly packed. This fast, high-frequency vibration is what our ears perceive as a sharp, high pitch.",
        "narration": "Now compare it to the red wave, where the peaks are tightly packed. This fast, high-frequency vibration is what our ears perceive as a sharp, high pitch.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/lH5vPSk6WCkOrieKpjRKhdHg9m2djdkImuqWTVw7.wav",
        "durationMs": 10651
    },
    {
        "type": "say",
        "order": 14,
        "slide_no": 5,
        "fragment_no": 3,
        "slug": "sw-pitch",
        "text": "So the rule is simple and direct. Faster vibrations mean a higher frequency, which creates a higher pitch.",
        "narration": "So the rule is simple and direct. Faster vibrations mean a higher frequency, which creates a higher pitch.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/8cGimttUcVnK3GGfDTud8bA2MsT4wYBqPTMbmKpN.wav",
        "durationMs": 7611
    },
    {
        "type": "say",
        "order": 15,
        "slide_no": 6,
        "fragment_no": 0,
        "slug": "sw-amplitude",
        "text": "Shifting our focus to volume brings us to the concept of amplitude. Simply put, a sound's loudness is determined by the amplitude of its wave.",
        "narration": "Shifting our focus to volume brings us to the concept of amplitude. Simply put, a sound's loudness is determined by the amplitude of its wave.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/6A4dwTuBtnqLFLH9P2Y3EmmLR2yzS2wJZbBKsWmn.wav",
        "durationMs": 9091
    },
    {
        "type": "say",
        "order": 16,
        "slide_no": 6,
        "fragment_no": 1,
        "slug": "sw-amplitude",
        "text": "Notice how short this wave is from the center line to the peak? That small amplitude results in a very soft sound, like a gentle whisper.",
        "narration": "Notice how short this wave is from the center line to the peak? That small amplitude results in a very soft sound, like a gentle whisper.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/bM7K4lpkijeToNMKmWaH9dEPCqJl5aqbCEEpvsdq.wav",
        "durationMs": 8931
    },
    {
        "type": "say",
        "order": 17,
        "slide_no": 6,
        "fragment_no": 2,
        "slug": "sw-amplitude",
        "text": "When we increase the energy, the wave stretches vertically and becomes much taller. As you'd guess, this larger amplitude creates a significantly louder sound.",
        "narration": "When we increase the energy, the wave stretches vertically and becomes much taller. As you'd guess, this larger amplitude creates a significantly louder sound.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/je34K7FWiVjdFM2gXu3c6UMi5nhGO8vnsvgLqbU8.wav",
        "durationMs": 10691
    },
    {
        "type": "say",
        "order": 18,
        "slide_no": 6,
        "fragment_no": 3,
        "slug": "sw-amplitude",
        "text": "Mathematically, loudness is proportional to the square of the amplitude. So if you double the amplitude, the sound actually becomes four times as intense!",
        "narration": "Mathematically, loudness is proportional to the square of the amplitude. So if you double the amplitude, the sound actually becomes four times as intense!",
        "audio": "https://asset.sparkl.me/pb/poc-folder/UKEJbeFB4kav7fi1sdFhKouXVuTgtfeqPCZ5sTWz.wav",
        "durationMs": 9691
    },
    {
        "type": "say",
        "order": 19,
        "slide_no": 7,
        "fragment_no": 0,
        "slug": "sw-timbre",
        "text": "Timbre is essentially the unique signature of a sound. A tuning fork produces the smooth, pure sine wave you see here because it consists of just a single frequency.",
        "narration": "Timbre is essentially the unique signature of a sound. A tuning fork produces the smooth, pure sine wave you see here because it consists of just a single frequency.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/sMW5jSKFf57BSAiOfBTTeLli1A9zaeMg6MFFsRHD.wav",
        "durationMs": 10011
    },
    {
        "type": "say",
        "order": 20,
        "slide_no": 7,
        "fragment_no": 1,
        "slug": "sw-timbre",
        "text": "Real instruments produce complex waves with ripples and bumps. This unique shape — a mix of the fundamental note and its harmonics — is what determines the instrument's distinct timbre.",
        "narration": "Real instruments produce complex waves with ripples and bumps. This unique shape — a mix of the fundamental note and its harmonics — is what determines the instrument's distinct timbre.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/8euRwJkP113AqrQmxrYchifXhOKALJRcHcczoOtE.wav",
        "durationMs": 11971
    },
    {
        "type": "say",
        "order": 21,
        "slide_no": 8,
        "fragment_no": 0,
        "slug": "sw-wave-equation",
        "text": "Let's connect the dots to derive the famous Wave Equation. We'll start with a familiar concept: speed is simply distance covered per unit of time.",
        "narration": "Let's connect the dots to derive the famous Wave Equation. We'll start with a familiar concept: speed is simply distance covered per unit of time.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/AzqGWP1msCCTmzGzvuoyeFOLMqZ22MIuJAbeNg70.wav",
        "durationMs": 11131
    },
    {
        "type": "say",
        "order": 22,
        "slide_no": 8,
        "fragment_no": 1,
        "slug": "sw-wave-equation",
        "text": "For a single wave cycle, the distance traveled is the wavelength, Lambda. The time it takes to complete that cycle is the Time Period, T.",
        "narration": "For a single wave cycle, the distance traveled is the wavelength, Lambda. The time it takes to complete that cycle is the Time Period, T.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/doyLfJDXg4UYnzRBwCWuvDAtXlLTnPRh57URBA6G.wav",
        "durationMs": 9171
    },
    {
        "type": "say",
        "order": 23,
        "slide_no": 8,
        "fragment_no": 2,
        "slug": "sw-wave-equation",
        "text": "By substituting these values into our basic speed formula, we see that velocity equals wavelength divided by the time period.",
        "narration": "By substituting these values into our basic speed formula, we see that velocity equals wavelength divided by the time period.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/ttXxAk5XDEaVYarBKrVbd6u1aiXvX3KlPkeDWzNA.wav",
        "durationMs": 7811
    },
    {
        "type": "say",
        "order": 24,
        "slide_no": 8,
        "fragment_no": 3,
        "slug": "sw-wave-equation",
        "text": "Since frequency is the reciprocal of the time period, we can swap it in to get the final Wave Equation. Speed equals wavelength times frequency.",
        "narration": "Since frequency is the reciprocal of the time period, we can swap it in to get the final Wave Equation. Speed equals wavelength times frequency.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/p0InqSh3Yk02dcKs3JJE2grvtoJu8eSCq4UmUx1G.wav",
        "durationMs": 8811
    },
    {
        "type": "say",
        "order": 25,
        "slide_no": 9,
        "fragment_no": 0,
        "slug": "sw-worked-example",
        "text": "Let's see this equation in practice. We need to calculate how long it takes a sound wave with a two kilohertz frequency and thirty-five centimeter wavelength to travel one point five kilometers.",
        "narration": "Let's see this equation in practice. We need to calculate how long it takes a sound wave with a two kilohertz frequency and thirty-five centimeter wavelength to travel one point five kilometers.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/U8TJpO2XTOhOPt3drUqCkd8FWbRyy9psq0SP8Pdq.wav",
        "durationMs": 12691
    },
    {
        "type": "say",
        "order": 26,
        "slide_no": 9,
        "fragment_no": 1,
        "slug": "sw-worked-example",
        "text": "First, we must convert our given values into standard SI units. Two kilohertz becomes two thousand Hertz, thirty-five centimeters becomes zero point three five meters, and one point five kilometers is fifteen hundred meters.",
        "narration": "First, we must convert our given values into standard SI units. Two kilohertz becomes two thousand Hertz, thirty-five centimeters becomes zero point three five meters, and one point five kilometers is fifteen hundred meters.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/Cn4iZMSUdL3TsK6ClwNNtfZfAD0DPFUJIiG97mP1.wav",
        "durationMs": 17491
    },
    {
        "type": "say",
        "order": 27,
        "slide_no": 9,
        "fragment_no": 2,
        "slug": "sw-worked-example",
        "text": "Now we multiply our wavelength of zero point three five meters by the two thousand Hertz frequency. That gives us a wave speed of seven hundred meters per second.",
        "narration": "Now we multiply our wavelength of zero point three five meters by the two thousand Hertz frequency. That gives us a wave speed of seven hundred meters per second.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/3Tt3tEdhIWdJaCuObm9DexIYs1mrGeU08mixf8GU.wav",
        "durationMs": 11691
    },
    {
        "type": "say",
        "order": 28,
        "slide_no": 9,
        "fragment_no": 3,
        "slug": "sw-worked-example",
        "text": "To find the time, we just divide our total distance of fifteen hundred meters by that speed of seven hundred. We get a final answer of approximately two point one four seconds.",
        "narration": "To find the time, we just divide our total distance of fifteen hundred meters by that speed of seven hundred. We get a final answer of approximately two point one four seconds.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/AhbB7g1Vq0HRM4Kp2AcvCXXaZ0HsmCJAVyWSHLx6.wav",
        "durationMs": 10331
    },
    {
        "type": "say",
        "order": 29,
        "slide_no": 10,
        "fragment_no": 0,
        "slug": "sw-simulator",
        "text": "Adjust the sliders to match the target wave and see these properties in action. Notice how increasing the frequency crowds the waves together, proving that inverse relationship with wavelength!",
        "narration": "Adjust the sliders to match the target wave and see these properties in action. Notice how increasing the frequency crowds the waves together, proving that inverse relationship with wavelength!",
        "audio": "https://asset.sparkl.me/pb/poc-folder/acfY4ZttgISwsYJ2keQALf8alLLmVV1RiS8525P7.wav",
        "durationMs": 10971
    },
    {
        "type": "say",
        "order": 30,
        "slide_no": 11,
        "fragment_no": 0,
        "slug": "sw-recap",
        "text": "Let's connect these technical terms back to our 'dancing paint' visual. It's the perfect way to see these invisible waves, as the paint reacts directly to the sound's physics.",
        "narration": "Let's connect these technical terms back to our 'dancing paint' visual. It's the perfect way to see these invisible waves, as the paint reacts directly to the sound's physics.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/SdMyAvlb0zZQYZsW1Jdp0kJW4QgL7b8dQNcQAlk7.wav",
        "durationMs": 11171
    },
    {
        "type": "say",
        "order": 31,
        "slide_no": 11,
        "fragment_no": 1,
        "slug": "sw-recap",
        "text": "Focus on the paint's jump height, which represents amplitude. A bigger physical motion translates directly to a louder sound for your ears.",
        "narration": "Focus on the paint's jump height, which represents amplitude. A bigger physical motion translates directly to a louder sound for your ears.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/rkPam0skHsTdUsA1aAhbknmGnsixJOawDe4WDdJE.wav",
        "durationMs": 9011
    },
    {
        "type": "say",
        "order": 32,
        "slide_no": 11,
        "fragment_no": 2,
        "slug": "sw-recap",
        "text": "Now look at the speed of the dance, which is the frequency. A faster vibration gives you high pitch notes, while a slower dance produces deep bass.",
        "narration": "Now look at the speed of the dance, which is the frequency. A faster vibration gives you high pitch notes, while a slower dance produces deep bass.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/ijA6jHcA5q6Fm38nKr3s4HcmcfrosB8G82At4Gex.wav",
        "durationMs": 10291
    },
    {
        "type": "say",
        "order": 33,
        "slide_no": 11,
        "fragment_no": 3,
        "slug": "sw-recap",
        "text": "It's just these two properties working in tandem. Together, the height and speed create the unique identity of every single sound we hear.",
        "narration": "It's just these two properties working in tandem. Together, the height and speed create the unique identity of every single sound we hear.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/P6BLJEuErtr1crLoxOndRZZHoj5keGzdSlR7SLpQ.wav",
        "durationMs": 9011
    },
    {
        "type": "end_class",
        "order": 34,
        "slide_no": 11,
        "fragment_no": 0,
        "narration": "Great work today, class! That wraps up our lesson on Characteristics of Sound Waves.",
        "audio": "https://asset.sparkl.me/pb/poc-folder/fGrgCH8XDVLwSOHChstwcEeNdtg7Oin9tMR6Eowm.wav",
        "durationMs": 5411
    }
];

export const soundWavesLesson = {
    id: "characteristics-of-sound-waves",
    sessionName: "Characteristics of Sound Waves",
    title: "Characteristics of Sound Waves",
    subtitle: "Amplitude, frequency, pitch, timbre — the anatomy of every note",
    grade: "Grade 9 · Physics",
    chapter: "Sound · Wave Characteristics",
    subject: "Physics",
    coverImage:
        "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=1600&q=85",
    coverAlt: "Stereo speaker producing visible sound vibrations",

    slides: SLIDES,

    sections: [
        {
            id: "concept",
            title: "Concept",
            description: "Auto-flows unless paused",
            slideIds: [
                "sw-hook",
                "sw-longitudinal",
                "sw-wavelength",
                "sw-frequency",
                "sw-pitch",
                "sw-amplitude",
                "sw-timbre",
                "sw-wave-equation",
                "sw-worked-example",
                "sw-simulator",
            ],
        },
        {
            id: "concept-check",
            title: "Concept Check",
            description: "5 MCQs",
            slideIds: ["mcq-display"],
        },
        {
            id: "practice",
            title: "Practice",
            description: "Subjective questions",
            slideIds: ["subjective-display"],
        },
        {
            id: "recap",
            title: "Recap",
            description: "Key takeaways",
            slideIds: ["sw-recap"],
        },
    ],

    // Concept Check / Practice / Real-life / Activity blocks populated with the real-life examples
    mcqs: [
        {
            id: "CQ_SOUND_01",
            concept: "Medium of sound",
            question: "A person can hear the vibration of an approaching train through a metal railway track before hearing it clearly through air. What does this show?",
            q: "A person can hear the vibration of an approaching train through a metal railway track before hearing it clearly through air. What does this show?",
            options: [
                "Sound can travel only through air.",
                "Sound needs a medium to travel, and solids can carry sound effectively.",
                "Sound cannot travel through solids.",
                "Sound travels only when there is light."
            ],
            correctIndex: 1,
            correct_option_id: "B",
            explain: "Sound travels through a medium. Since particles in solids are closely packed, vibrations can pass through them effectively.",
            explanation: "Sound travels through a medium. Since particles in solids are closely packed, vibrations can pass through them effectively."
        },
        {
            id: "CQ_SOUND_02",
            concept: "Compression and rarefaction",
            question: "When a speaker cone moves back and forth, it creates crowded and spread-out regions of air particles. What are these regions called?",
            q: "When a speaker cone moves back and forth, it creates crowded and spread-out regions of air particles. What are these regions called?",
            options: [
                "Crests and troughs",
                "Reflections and echoes",
                "Compressions and rarefactions",
                "Amplitude and pitch"
            ],
            correctIndex: 2,
            correct_option_id: "C",
            explain: "In a sound wave, crowded particle regions are compressions, and spread-out particle regions are rarefactions.",
            explanation: "In a sound wave, crowded particle regions are compressions, and spread-out particle regions are rarefactions."
        },
        {
            id: "CQ_SOUND_03",
            concept: "Amplitude and loudness",
            question: "At a concert, you can sometimes feel the bass sound vibrating in your chest. Which property of the sound wave is mainly responsible for this strong effect?",
            q: "At a concert, you can sometimes feel the bass sound vibrating in your chest. Which property of the sound wave is mainly responsible for this strong effect?",
            options: [
                "Large amplitude",
                "Small amplitude",
                "No vibration",
                "Zero frequency"
            ],
            correctIndex: 0,
            correct_option_id: "A",
            explain: "Loud sounds have larger amplitude, which means stronger vibrations and more energy.",
            explanation: "Loud sounds have larger amplitude, which means stronger vibrations and more energy."
        },
        {
            id: "CQ_SOUND_04",
            concept: "Frequency and pitch",
            question: "A mosquito produces a sharp buzzing sound because its wings vibrate very fast. Which sound characteristic explains this?",
            q: "A mosquito produces a sharp buzzing sound because its wings vibrate very fast. Which sound characteristic explains this?",
            options: [
                "High amplitude produces low pitch.",
                "High frequency produces high pitch.",
                "Low frequency produces high pitch.",
                "Sound pitch does not depend on vibration."
            ],
            correctIndex: 1,
            correct_option_id: "B",
            explain: "Frequency is the number of vibrations per second. Faster vibrations mean higher frequency, which is heard as higher pitch.",
            explanation: "Frequency is the number of vibrations per second. Faster vibrations mean higher frequency, which is heard as higher pitch."
        },
        {
            id: "CQ_SOUND_05",
            concept: "Wavelength",
            question: "You are in another room and can still hear the deep bass from music, but the lyrics are not very clear. Why does bass travel through walls more easily?",
            q: "You are in another room and can still hear the deep bass from music, but the lyrics are not very clear. Why does bass travel through walls more easily?",
            options: [
                "Bass sounds usually have longer wavelengths.",
                "Bass sounds do not need a medium.",
                "Bass sounds always travel as light waves.",
                "Bass sounds have no frequency."
            ],
            correctIndex: 0,
            correct_option_id: "A",
            explain: "Low-frequency bass sounds have longer wavelengths, which helps them bend around obstacles and pass through barriers more easily.",
            explanation: "Low-frequency bass sounds have longer wavelengths, which helps them bend around obstacles and pass through barriers more easily."
        }
    ],
    subjectives: [],
    realLife: [
        {
            "id": "sound_real_life_01",
            "headline": "Why Can You Hear a Train Through the Tracks?",
            "description": "In old movies, people place their ear on railway tracks to hear a train before it is visible. The sound vibrations travel through the metal track faster and more clearly than through air.\n\n💡 Teaching Point: Sound needs a medium to travel. It can travel through solids, liquids, and gases, but not through vacuum. Solids often carry sound better because particles are closely packed.",
            "image": "https://asset.sparkl.me/pb/poc-folder/BXOg7zg6XFMRKwGMYVY8lORd9YJlJw8qHy0geLzC.png"
        },
        {
            "id": "sound_real_life_02",
            "headline": "A Speaker Pushing Air",
            "description": "When a speaker plays music, its cone moves forward and backward rapidly. Each forward push squeezes air particles together, and each backward movement creates a spread-out region.\n\n💡 Teaching Point: These alternating crowded and spread-out regions are compressions and rarefactions. This is how sound moves as a longitudinal wave.",
            "image": "https://asset.sparkl.me/pb/poc-folder/Oy52DWaZ11iIzhn7BAf3NX8xXxRiSmpArKSl3UjL.png"
        },
        {
            "id": "sound_real_life_03",
            "headline": "Why Does a Loudspeaker Make Your Chest Feel the Beat?",
            "description": "At a concert, a powerful bass speaker does not just sound loud — you can sometimes feel the vibration in your chest. That happens because loud sounds carry larger vibrations.\n\n💡 Teaching Point: Greater amplitude means stronger vibration and louder sound. The wave carries more energy.",
            "image": "https://asset.sparkl.me/pb/poc-folder/Atd0S0OO1gKkH4gaN9zfdvOI9Ks90zlW7EyMQILh.png"
        },
        {
            "id": "sound_real_life_04",
            "headline": "Why Does a Mosquito Sound So Annoying?",
            "description": "A mosquito makes a high-pitched buzzing sound because its wings flap very fast. Faster vibrations create a higher frequency sound.\n\n💡 Teaching Point: Frequency means the number of vibrations per second. Higher frequency is heard as higher pitch.",
            "image": "https://asset.sparkl.me/pb/poc-folder/87AC6aFFxqpX4XwdhxHndC83PFIzujyqCeMIQZAR.png"
        },
        {
            "id": "sound_real_life_05",
            "headline": "Why Can Bass Travel Through Walls?",
            "description": "When music is playing in another room, you may not hear the lyrics clearly, but you can still hear the deep bass. Low-frequency sounds have longer wavelengths, so they bend around obstacles and pass through walls more easily.\n\n💡 Teaching Point: Longer wavelength sounds, like bass, can travel around or through obstacles better than shorter wavelength sounds.",
            "image": "https://asset.sparkl.me/pb/poc-folder/0arNEtFhVYU5MgK0geGzEmNCDTCy3pJKS0URPbgd.png"
        }
    ],
    realLifeExamples: [
        {
            "id": "sound_real_life_01",
            "text": "Sound travels through a medium",
            "headline": "Why Can You Hear a Train Through the Tracks?",
            "description": "In old movies, people place their ear on railway tracks to hear a train before it is visible. The sound vibrations travel through the metal track faster and more clearly than through air.\n\n💡 Teaching Point: Sound needs a medium to travel. It can travel through solids, liquids, and gases, but not through vacuum. Solids often carry sound better because particles are closely packed.",
            "image": "https://asset.sparkl.me/pb/poc-folder/BXOg7zg6XFMRKwGMYVY8lORd9YJlJw8qHy0geLzC.png"
        },
        {
            "id": "sound_real_life_02",
            "text": "Compression and rarefaction",
            "headline": "A Speaker Pushing Air",
            "description": "When a speaker plays music, its cone moves forward and backward rapidly. Each forward push squeezes air particles together, and each backward movement creates a spread-out region.\n\n💡 Teaching Point: These alternating crowded and spread-out regions are compressions and rarefactions. This is how sound moves as a longitudinal wave.",
            "image": "https://asset.sparkl.me/pb/poc-folder/Oy52DWaZ11iIzhn7BAf3NX8xXxRiSmpArKSl3UjL.png"
        },
        {
            "id": "sound_real_life_03",
            "text": "Amplitude and loudness",
            "headline": "Why Does a Loudspeaker Make Your Chest Feel the Beat?",
            "description": "At a concert, a powerful bass speaker does not just sound loud — you can sometimes feel the vibration in your chest. That happens because loud sounds carry larger vibrations.\n\n💡 Teaching Point: Greater amplitude means stronger vibration and louder sound. The wave carries more energy.",
            "image": "https://asset.sparkl.me/pb/poc-folder/Atd0S0OO1gKkH4gaN9zfdvOI9Ks90zlW7EyMQILh.png"
        },
        {
            "id": "sound_real_life_04",
            "text": "Frequency and pitch",
            "headline": "Why Does a Mosquito Sound So Annoying?",
            "description": "A mosquito makes a high-pitched buzzing sound because its wings flap very fast. Faster vibrations create a higher frequency sound.\n\n💡 Teaching Point: Frequency means the number of vibrations per second. Higher frequency is heard as higher pitch.",
            "image": "https://asset.sparkl.me/pb/poc-folder/87AC6aFFxqpX4XwdhxHndC83PFIzujyqCeMIQZAR.png"
        },
        {
            "id": "sound_real_life_05",
            "text": "Wavelength",
            "headline": "Why Can Bass Travel Through Walls?",
            "description": "When music is playing in another room, you may not hear the lyrics clearly, but you can still hear the deep bass. Low-frequency sounds have longer wavelengths, so they bend around obstacles and pass through walls more easily.\n\n💡 Teaching Point: Longer wavelength sounds, like bass, can travel around or through obstacles better than shorter wavelength sounds.",
            "image": "https://asset.sparkl.me/pb/poc-folder/0arNEtFhVYU5MgK0geGzEmNCDTCy3pJKS0URPbgd.png"
        }
    ],
    recommendations: {},
    teacherHelp: {},
};

export default soundWavesLesson;

